/**
 * Modal with troubleshooting tips for push notifications
 */

import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef } from "react";

interface PushNotificationHelpModalProps {
    isOpen: boolean;
    onClose: () => void;
    variant?: "village" | "longhouse";
}

export function PushNotificationHelpModal({
    isOpen,
    onClose,
    variant = "village",
}: PushNotificationHelpModalProps) {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const getThemeClasses = () => {
        if (variant === "village") {
            return {
                overlay: "bg-black/70",
                modal: "bg-gray-900 border-brand/30",
                title: "text-brand",
                text: "text-slate-300",
                button: "bg-brand/20 text-brand hover:bg-brand/30 border-brand/40",
                code: "bg-black/40 text-brand-light border-brand/20",
            };
        } else {
            return {
                overlay: "bg-black/50",
                modal: "bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700",
                title: "text-blue-600 dark:text-blue-400",
                text: "text-gray-700 dark:text-slate-300",
                button: "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/40 border-blue-300 dark:border-blue-700",
                code: "bg-gray-100 dark:bg-black/40 text-gray-800 dark:text-blue-300 border-gray-300 dark:border-blue-500/20",
            };
        }
    };

    const theme = getThemeClasses();

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme.overlay}`}
            onClick={onClose}
        >
            <div
                ref={modalRef}
                className={`relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border ${theme.modal} p-6 shadow-xl`}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className={`absolute top-4 right-4 rounded p-1 ${theme.button} border`}
                    aria-label="Close"
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>

                <h2 className={`mb-4 text-2xl font-bold ${theme.title}`}>
                    Push Notifications Help
                </h2>

                <div className={`space-y-6 ${theme.text}`}>
                    <section>
                        <h3 className="mb-2 text-lg font-semibold">
                            Not Seeing Notifications?
                        </h3>
                        <p className="mb-3">
                            If you've enabled push notifications but aren't
                            seeing popups, try these troubleshooting steps:
                        </p>
                    </section>

                    <section>
                        <h4 className="mb-2 font-semibold">
                            macOS Users (Chrome, Edge, Brave):
                        </h4>
                        <ol className="ml-5 list-decimal space-y-2">
                            <li>
                                Open <strong>System Settings</strong> →{" "}
                                <strong>Notifications</strong>
                            </li>
                            <li>
                                Find your browser (Chrome, Edge, etc.) in the
                                list
                            </li>
                            <li>
                                Make sure <strong>Allow Notifications</strong>{" "}
                                is ON
                            </li>
                            <li>
                                Set notification style to{" "}
                                <strong>Banners</strong> (not Alerts)
                            </li>
                            <li>
                                Check <strong>Show previews</strong> is set to
                                "Always" or "When Unlocked"
                            </li>
                            <li>
                                Disable <strong>Do Not Disturb</strong> and{" "}
                                <strong>Focus</strong> modes
                            </li>
                        </ol>
                    </section>

                    <section>
                        <h4 className="mb-2 font-semibold">
                            Windows Users (Chrome, Edge):
                        </h4>
                        <ol className="ml-5 list-decimal space-y-2">
                            <li>
                                Open <strong>Windows Settings</strong> →{" "}
                                <strong>System</strong> →{" "}
                                <strong>Notifications</strong>
                            </li>
                            <li>
                                Make sure notifications are enabled for your
                                browser
                            </li>
                            <li>
                                Check if <strong>Focus Assist</strong> is OFF
                            </li>
                            <li>
                                In Chrome: Click the lock icon in the address
                                bar → Permissions → Notifications → Allow
                            </li>
                        </ol>
                    </section>

                    <section>
                        <h4 className="mb-2 font-semibold">
                            Linux Users (Chrome, Firefox):
                        </h4>
                        <ol className="ml-5 list-decimal space-y-2">
                            <li>
                                Check your system notification settings (varies
                                by distro)
                            </li>
                            <li>Ensure your notification daemon is running</li>
                            <li>
                                For Chrome: Check{" "}
                                <code className={`rounded px-1 ${theme.code}`}>
                                    chrome://settings/content/notifications
                                </code>
                            </li>
                        </ol>
                    </section>

                    <section>
                        <h4 className="mb-2 font-semibold">
                            Test Notifications:
                        </h4>
                        <p className="mb-2">
                            Open your browser console (F12) and run:
                        </p>
                        <pre
                            className={`overflow-x-auto rounded border p-3 text-sm ${theme.code}`}
                        >
                            {`new Notification('Test', {
  body: 'If you see this, notifications work!',
  icon: '/web-app-manifest-192x192.png'
});`}
                        </pre>
                        <p className="mt-2">
                            If this shows a popup, the system is working and
                            it's a browser/OS settings issue.
                        </p>
                    </section>

                    <section>
                        <h4 className="mb-2 font-semibold">
                            Still Not Working?
                        </h4>
                        <ul className="ml-5 list-disc space-y-1">
                            <li>Try a different browser (Firefox, Safari)</li>
                            <li>Restart your browser completely</li>
                            <li>
                                Check if your browser is up to date (push
                                notifications require modern browser versions)
                            </li>
                            <li>
                                Clear browser cache and re-enable notifications
                            </li>
                        </ul>
                    </section>

                    <section className="border-t border-current/20 pt-4">
                        <p className="text-sm italic">
                            <strong>Note:</strong> Push notifications work best
                            when the browser is minimized or you're in another
                            app. When the inbox page is focused, you'll see
                            in-app notifications instead of popups.
                        </p>
                    </section>
                </div>

                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className={`rounded border px-4 py-2 font-medium ${theme.button}`}
                    >
                        Got it!
                    </button>
                </div>
            </div>
        </div>
    );
}
